import { stakeholderApi, stakeholderEndpoints, STAKEHOLDER_API_URL } from '@/config/stakeholder-api';
import { ApiResponse } from '@/config/api';
import { getIdToken } from '@/config/firebase';

// Types
export interface Stakeholder {
  id: string;
  name: string;
  title: string;
  department: string;
  bio?: string;
  avatar_url?: string;
  email?: string;
  phone?: string;
  is_active: boolean;
  openness_level: number;
  personality_traits: string[];
  knowledge_areas: Array<{
    category: string;
    expertise: string;
  }>;
  // Legacy fields for backward compatibility
  imageUrl?: string;
  availability?: 'high' | 'medium' | 'low';
  expertise?: string[];
  isActive?: boolean;
  personality?: {
    openness: number;
    enthusiasm: number;
    assertiveness: number;
    criticalThinking: number;
    detailOrientation: number;
  };
  communicationStyle?: {
    formalityLevel: string;
    jargonUsage: string;
    responseLength: string;
    emotionalTone: string;
  };
  knowledgeAreas?: Array<{
    category: string;
    subcategory: string;
    expertise: string;
    willingnessToShare: number;
  }>;
}

export interface Conversation {
  id: string;
  userId: string;
  stakeholderId: string;
  status: 'active' | 'completed' | 'abandoned';
  startedAt: Date;
  endedAt?: Date;
  messageCount: number;
  trustLevel: number;
  topicsDiscussed: string[];
  revealedConcerns: string[];
  metadata: {
    duration?: number;
    userSatisfaction?: number;
    stakeholderName: string;
    stakeholderTitle: string;
    gapsDiscovered?: number;
    pointsEarned?: number;
  };
}

export interface Message {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  metadata?: {
    trustDelta?: number;
    revealedInfo?: string[];
    emotionalTone?: string;
    modelProvider?: string;
    modelName?: string;
    vertexAiMetadata?: {
      modelVersion: string;
      tokensUsed: number;
      latency: number;
    };
  };
}

// Single quiz question (from multi-question quiz)
export interface QuizQuestionItem {
  gapId: string;
  gapTitle: string;
  question: string;
  choices: Array<{ id: string; text: string; isCorrect: boolean }>;
  hint: string;
  explanation: string;
}

// Multi-question quiz from backend
export interface Quiz {
  id: string;
  questions: QuizQuestionItem[];
  currentIndex: number;
  metadata: {
    totalPoints: number;
    pointsPerQuestion: number;
  };
}

export interface ProgressUpdate {
  totalPoints: number;
  discoveredGaps: string[];
  completionPercentage: number;
  newGapId?: string;
}

export interface SendMessageResponse {
  userMessage: Message;
  assistantMessage: Message;
  metadata: {
    trustLevel: number;
    tokensUsed: number;
    estimatedCost: number;
  };
  quiz?: Quiz;
  progressUpdate?: ProgressUpdate;
  coachingHints?: string[];
}

// API Service
export const stakeholderService = {
  // Get all stakeholders
  getStakeholders: async (department?: string): Promise<Stakeholder[]> => {
    console.log('[StakeholderService] Fetching stakeholders, department:', department);
    try {
      const params = department ? { params: { department } } : undefined;
      console.log('[StakeholderService] Making API call to:', stakeholderEndpoints.stakeholders.list);
      const response = await stakeholderApi.get<ApiResponse<Stakeholder[]>>(stakeholderEndpoints.stakeholders.list, params);
      console.log('[StakeholderService] API response:', response);

      // Handle various response formats
      let stakeholderData: Stakeholder[] = [];

      if (response.data) {
        if (response.data.success === false) {
          console.error('[StakeholderService] API returned success=false:', response.data);
          throw new Error(response.data.error?.message || 'Failed to fetch stakeholders');
        }

        if (Array.isArray(response.data.data)) {
          stakeholderData = response.data.data;
        } else if (response.data.data && typeof response.data.data === 'object') {
          // Handle nested object response
          if (Array.isArray(response.data.data.items)) {
            stakeholderData = response.data.data.items;
          } else if (Array.isArray(response.data.data.stakeholders)) {
            stakeholderData = response.data.data.stakeholders;
          } else if (Array.isArray(response.data.data.personas)) {
            stakeholderData = response.data.data.personas;
          }
        }
      } else if (Array.isArray(response.data)) {
        // Direct array response
        stakeholderData = response.data;
      }

      console.log('[StakeholderService] Extracted stakeholders count:', stakeholderData.length);
      console.log('[StakeholderService] Stakeholder IDs:', stakeholderData.map(s => ({ id: s.id, name: s.name })));
      return stakeholderData;
    } catch (error: any) {
      console.error('[StakeholderService] Error fetching stakeholders:', error);
      // Log more details about the error
      if (error.response) {
        console.error('[StakeholderService] Error response:', error.response.data);
        console.error('[StakeholderService] Error status:', error.response.status);
      }
      throw error;
    }
  },

  // Get stakeholder by ID
  getStakeholder: async (id: string): Promise<ApiResponse<Stakeholder>> => {
    const response = await stakeholderApi.get<ApiResponse<Stakeholder>>(stakeholderEndpoints.stakeholders.get(id));
    return response.data;
  },

  // Search stakeholders by knowledge
  searchStakeholders: async (category: string, minExpertise?: string): Promise<ApiResponse<Stakeholder[]>> => {
    const response = await stakeholderApi.get<ApiResponse<Stakeholder[]>>(stakeholderEndpoints.stakeholders.search, {
      params: { category, minExpertise }
    });
    return response.data;
  },

  // Get session for stakeholder (replaces createConversation)
  getSession: async (stakeholderId: string): Promise<any> => {
    const response = await stakeholderApi.get<ApiResponse<any>>(stakeholderEndpoints.sessions.get(stakeholderId));
    return response.data.data!;
  },

  // Get all user sessions (replaces getConversations)
  getSessions: async (): Promise<ApiResponse<any[]>> => {
    const response = await stakeholderApi.get<ApiResponse<any[]>>(stakeholderEndpoints.sessions.list);
    return response.data;
  },

  // Get conversations with optional filtering (wraps getSessions for Dashboard compatibility)
  getConversations: async (status?: string, limit?: number): Promise<ApiResponse<Conversation[]>> => {
    const response = await stakeholderApi.get<ApiResponse<any[]>>(stakeholderEndpoints.sessions.list);

    // Transform sessions to Conversation format and apply filters
    let conversations: Conversation[] = (response.data.data || []).map((session: any) => ({
      id: session.id || `${session.userId}-${session.stakeholderId}`,
      userId: session.userId,
      stakeholderId: session.stakeholderId,
      status: session.status || 'active',
      startedAt: new Date(session.createdAt || session.startedAt),
      endedAt: session.endedAt ? new Date(session.endedAt) : undefined,
      messageCount: session.messageCount || 0,
      trustLevel: Math.round((session.trustLevel || 0) * 100),
      topicsDiscussed: session.topicsDiscussed || [],
      revealedConcerns: session.revealedGaps || [],
      metadata: {
        duration: session.metadata?.duration,
        userSatisfaction: session.metadata?.userSatisfaction,
        stakeholderName: session.stakeholderName || session.metadata?.stakeholderName || 'Unknown',
        stakeholderTitle: session.stakeholderTitle || session.metadata?.stakeholderTitle || '',
        gapsDiscovered: session.revealedGaps?.length || 0,
        pointsEarned: session.metadata?.pointsEarned || 0,
      },
    }));

    // Filter by status if provided
    if (status) {
      conversations = conversations.filter(c => c.status === status);
    }

    // Limit results if provided
    if (limit && limit > 0) {
      conversations = conversations.slice(0, limit);
    }

    return {
      success: true,
      data: conversations,
    };
  },

  // Get messages for stakeholder (uses stakeholder ID instead of conversation ID)
  getMessages: async (stakeholderId: string, limit?: number): Promise<ApiResponse<Message[]>> => {
    const response = await stakeholderApi.get<ApiResponse<Message[]>>(stakeholderEndpoints.sessions.messages(stakeholderId), {
      params: { limit }
    });
    return response.data;
  },

  // Send message to stakeholder (uses stakeholder ID instead of conversation ID)
  sendMessage: async (stakeholderId: string, content: string): Promise<ApiResponse<SendMessageResponse>> => {
    const response = await stakeholderApi.post<ApiResponse<SendMessageResponse>>(stakeholderEndpoints.sessions.sendMessage(stakeholderId), { content });
    return response.data;
  },

  // Reset session for stakeholder (clears messages, trust, revealed gaps)
  resetSession: async (stakeholderId: string): Promise<ApiResponse<any>> => {
    const response = await stakeholderApi.post<ApiResponse<any>>(stakeholderEndpoints.sessions.reset(stakeholderId));
    return response.data;
  },

  // Reset ALL sessions for user (clears all stakeholder conversations, gaps, and progress)
  resetAllSessions: async (): Promise<ApiResponse<any>> => {
    const response = await stakeholderApi.post<ApiResponse<any>>(stakeholderEndpoints.sessions.resetAll);
    return response.data;
  },

  /**
   * Send message with streaming response (SSE)
   * @param stakeholderId - Target stakeholder ID
   * @param content - Message content
   * @param onContent - Callback for content chunks (for real-time display)
   * @param onToolStart - Callback when a tool starts executing
   * @param onToolEnd - Callback when a tool finishes
   * @param onDone - Callback with final metadata, quiz, etc.
   * @param onError - Callback for errors
   */
  sendMessageStreaming: async (
    stakeholderId: string,
    content: string,
    callbacks: {
      onContent?: (chunk: string) => void;
      onToolStart?: (data: { toolName: string; args: any }) => void;
      onToolEnd?: (data: { toolName: string; result: any }) => void;
      onDone?: (data: {
        metadata: { trustLevel: number; tokensUsed: number; estimatedCost: number; latency: number };
        quiz: any;
        progressUpdate: any;
        coachingHints: string[];
      }) => void;
      onError?: (error: string) => void;
      onUserMessage?: (message: Message) => void;
    }
  ): Promise<void> => {
    const token = await getIdToken();
    const url = `${STAKEHOLDER_API_URL}${stakeholderEndpoints.sessions.sendMessageStream(stakeholderId)}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ content }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      callbacks.onError?.(errorText || `HTTP ${response.status}`);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError?.('No response body');
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      let currentEvent = '';
      let currentData = '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          currentData = line.slice(6);
        } else if (line === '' && currentEvent && currentData) {
          // End of event, process it
          try {
            const data = JSON.parse(currentData);

            switch (currentEvent) {
              case 'user_message':
                callbacks.onUserMessage?.(data);
                break;
              case 'content':
                callbacks.onContent?.(data.chunk);
                break;
              case 'tool_start':
                callbacks.onToolStart?.(data);
                break;
              case 'tool_end':
                callbacks.onToolEnd?.(data);
                break;
              case 'done':
                callbacks.onDone?.(data);
                break;
              case 'error':
                callbacks.onError?.(data.message || 'Unknown error');
                break;
            }
          } catch (e) {
            console.error('[Streaming] Failed to parse SSE data:', currentData, e);
          }

          currentEvent = '';
          currentData = '';
        }
      }
    }
  },
};